<template>
  <div class="product-content" v-html="contentFieldsCollected"></div>
</template>

<script setup lang="ts">
  import { useMainStore } from '@/stores/main';

  // Define interfaces
  interface ContentField {
    external?: string;
    internal?: string;
  }

  interface ContentObject {
    shortDescr?: string;
    descr?: string;
    longDescr?: string;
    features?: string;
    standardFittings?: string;
    altFittings?: string;
    notaBene?: string;
    erpCode?: string;
    serviceInfo?: string;
    external?: {
      [key: string]: string;
    };
    internal?: {
      [key: string]: string;
    };
  }

  interface Props {
    content?: ContentObject;
  }

  const props = withDefaults(defineProps<Props>(), {
    content: () => ({})
  });

  const mainStore = useMainStore();
  const langJson = computed(() => mainStore.langJson);
  const lang = computed(() => mainStore.lang);

  function setProperFieldExternal(obj: ContentObject | undefined, field: string): string {
    if (!obj) return '';
    return (obj.external?.[field] || String((obj as Record<string, unknown>)[field] || '')) as string;
  }

  function setProperFieldInternal(obj: ContentObject | undefined, field: string): string {
    if (!obj) return '';
    return obj.internal?.[field] || '';
  }

  const shortDescr = computed(() => ({
    external: setProperFieldExternal(props.content, 'shortDescr'),
    internal: setProperFieldInternal(props.content, 'shortDescr')
  }));

  const descr = computed(() => ({
    external: setProperFieldExternal(props.content, 'descr'),
    internal: setProperFieldInternal(props.content, 'descr')
  }));

  const longDescr = computed(() => ({
    external: setProperFieldExternal(props.content, 'longDescr'),
    internal: setProperFieldInternal(props.content, 'longDescr')
  }));

  const features = computed(() => ({
    external: setProperFieldExternal(props.content, 'features'),
    internal: setProperFieldInternal(props.content, 'features')
  }));

  const standardFittings = computed(() => ({
    external: setProperFieldExternal(props.content, 'standardFittings'),
    internal: setProperFieldInternal(props.content, 'standardFittings')
  }));

  const altFittings = computed(() => ({
    external: setProperFieldExternal(props.content, 'altFittings'),
    internal: setProperFieldInternal(props.content, 'altFittings')
  }));

  const notaBene = computed(() => ({
    external: setProperFieldExternal(props.content, 'notaBene'),
    internal: setProperFieldInternal(props.content, 'notaBene')
  }));

  const erpCode = computed(() => props.content?.erpCode);
  const serviceInfo = computed(() => props.content?.serviceInfo);

  const contentFieldsCollected = computed(() => {
    const contentStart = '<div class="product-content__column">';
    let contentBody = '';
    const contentEnd = '</div>';

    const setParagraph = (text: string, internal: boolean, type: string): string => {
      if (internal) {
        if (type === 'service') {
          return '<div class="product-content__info service-information">' + text + '</div>';
        } else if (type === 'info') {
          return '<div class="product-content__info internal-information">' + text + '</div>';
        } else {
          // 'note' renders as plain grey __note regardless of source — DOV-1486:
          // both EXT and INT nota bene must use the same grey indication.
          return '<div class="product-content__note">' + text + '</div>';
        }
      } else {
        if (type === 'info') {
          return '<div class="product-content__info">' + text + '</div>';
        } else {
          return '<div class="product-content__note">' + text + '</div>';
        }
      }
    };

    const splitColumns = (text: string, internal: boolean, type: string): string => {
      if (internal) {
        if (type === 'info') {
          return text.replace(
            '||',
            '</div></div><div class="product-content__column"><div class="product-content__info internal-information">'
          );
        } else {
          // 'note' — grey __note regardless of source (DOV-1486).
          return text.replace('||', '</div></div><div class="product-content__column"><div class="product-content__note">');
        }
      } else {
        if (type === 'info') {
          return text.replace('||', '</div></div><div class="product-content__column"><div class="product-content__info">');
        } else {
          return text.replace('||', '</div></div><div class="product-content__column"><div class="product-content__note">');
        }
      }
    };

    const addString = (piece: string, whole: string): string => (whole += piece);

    if (longDescr.value.external) {
      const paragraph = setParagraph(longDescr.value.external, false, 'info');
      const column = splitColumns(paragraph, false, 'info');
      contentBody = addString(column, contentBody);
    }
    if (longDescr.value.internal) {
      const paragraph = setParagraph(longDescr.value.internal, true, 'info');
      const column = splitColumns(paragraph, true, 'info');
      contentBody = addString(column, contentBody);
    }
    if (shortDescr.value.external) {
      const paragraph = setParagraph(shortDescr.value.external, false, 'info');
      const column = splitColumns(paragraph, false, 'info');
      contentBody = addString(column, contentBody);
    }
    if (shortDescr.value.internal) {
      const paragraph = setParagraph(shortDescr.value.internal, true, 'info');
      const column = splitColumns(paragraph, true, 'info');
      contentBody = addString(column, contentBody);
    }
    if (descr.value.external) {
      const paragraph = setParagraph(descr.value.external, false, 'info');
      const column = splitColumns(paragraph, false, 'info');
      contentBody = addString(column, contentBody);
    }
    if (descr.value.internal) {
      const paragraph = setParagraph(descr.value.internal, true, 'info');
      const column = splitColumns(paragraph, true, 'info');
      contentBody = addString(column, contentBody);
    }
    if (features.value.external || features.value.internal) {
      if (
        features.value.external &&
        !features.value.external.includes('||') &&
        features.value.internal &&
        !features.value.internal.includes('||')
      ) {
        const paragraph = setParagraph(
          "<div class='sub-heading'>" + (langJson.value[lang.value]?.features || '') + '</div>',
          false,
          'info'
        );
        const column = splitColumns(paragraph, false, 'info');
        contentBody = addString(column, contentBody);
      }
    }
    if (features.value.external) {
      const paragraph = setParagraph(features.value.external, false, 'info');
      const column = splitColumns(paragraph, false, 'info');
      contentBody = addString(column, contentBody);
    }
    if (features.value.internal) {
      const paragraph = setParagraph(features.value.internal, true, 'info');
      const column = splitColumns(paragraph, true, 'info');
      contentBody = addString(column, contentBody);
    }
    if (standardFittings.value.external || standardFittings.value.internal) {
      const paragraph = setParagraph(
        "<div class='sub-heading'>" + (langJson.value[lang.value]?.standardFittings || '') + '</div>',
        false,
        'info'
      );
      const column = splitColumns(paragraph, false, 'info');
      contentBody = addString(column, contentBody);
    }
    if (standardFittings.value.external) {
      const paragraph = setParagraph(standardFittings.value.external, false, 'info');
      const column = splitColumns(paragraph, false, 'info');
      contentBody = addString(column, contentBody);
    }
    if (standardFittings.value.internal) {
      const paragraph = setParagraph(standardFittings.value.internal, true, 'info');
      const column = splitColumns(paragraph, true, 'info');
      contentBody = addString(column, contentBody);
    }
    if (altFittings.value.external || altFittings.value.internal) {
      const paragraph = setParagraph(
        "<div class='sub-heading'>" + (langJson.value[lang.value]?.alternativeFittings || '') + '</div>',
        false,
        'info'
      );
      const column = splitColumns(paragraph, false, 'info');
      contentBody = addString(column, contentBody);
    }
    if (altFittings.value.external) {
      const paragraph = setParagraph(altFittings.value.external, false, 'info');
      const column = splitColumns(paragraph, false, 'info');
      contentBody = addString(column, contentBody);
    }
    if (altFittings.value.internal) {
      const paragraph = setParagraph(altFittings.value.internal, true, 'info');
      const column = splitColumns(paragraph, true, 'info');
      contentBody = addString(column, contentBody);
    }
    if (notaBene.value.external) {
      const paragraph = setParagraph(notaBene.value.external, false, 'note');
      const column = splitColumns(paragraph, false, 'note');
      contentBody = addString(column, contentBody);
    }
    if (notaBene.value.internal) {
      const paragraph = setParagraph(notaBene.value.internal, true, 'note');
      const column = splitColumns(paragraph, true, 'note');
      contentBody = addString(column, contentBody);
    }
    if (erpCode.value) {
      const paragraph = setParagraph(erpCode.value, true, 'info');
      const column = splitColumns(paragraph, true, 'info');
      contentBody = addString(column, contentBody);
    }
    if (serviceInfo.value) {
      const paragraphHeader = setParagraph(
        "<div class='sub-heading'>" + (langJson.value[lang.value]?.serviceInformation || '') + '</div>',
        false,
        'info'
      );
      const columnHeader = splitColumns(paragraphHeader, false, 'info');
      contentBody = addString(columnHeader, contentBody);
      const paragraph = setParagraph(serviceInfo.value, true, 'service');
      const column = splitColumns(paragraph, true, 'service');
      contentBody = addString(column, contentBody);
    }
    return contentStart + contentBody + contentEnd;
  });
</script>
